import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  useDrawer,
  Typography,
  tokens,
} from '@cast/design-system';

import { AppliedPodsTable } from '../../../_components/AppliedPodsTable/AppliedPodsTable';
import { useCurrentWorkloadMetrics } from '../../../_hooks/useCurrentWorkloadMetrics';

import { SearchProvider, Search } from '@cast/shared-search-ui';

type BaseProps = {
  workloadId: string;
  workloadName: string;
  totalPods: number;
  appliedPods: number;
  // True when BE returns recommendationStatus.type === STATUS_UNKNOWN for the
  // parent workload (vertical autoscaling disabled). See _workloadStatusUtils
  // for the source-of-truth check; the opener computes it from the
  // PreparedWorkload it already has and passes it in.
  isOptimizationDisabled?: boolean;
};
type Props = DrawerProps & BaseProps;

const AppliedPodsDrawer = ({
  workloadId,
  workloadName,
  totalPods,
  appliedPods,
  isOptimizationDisabled,
  ref,
  ...props
}: Props) => {
  const { pods, isLoading } = useCurrentWorkloadMetrics({
    workloadId,
    includeMetrics: false,
    includeCosts: false,
    refetchInterval: () => 30_000,
  });

  return (
    <Drawer ref={ref} size="large" testId="applied-pods-drawer" {...props}>
      <DrawerHeader />
      <DrawerBody testId="applied-pods-drawer-body">
        <DrawerContent>
          <Typography
            variant="L12R"
            color={tokens.theme.fg.structural.secondary}
            mb={8}
            data-testid="applied-pods-drawer-primary-header"
          >
            {workloadName}:
          </Typography>
          <Typography
            variant="h5"
            mb={24}
            data-testid="applied-pods-drawer-secondary-header"
          >
            Applied pods {appliedPods}/{totalPods}
          </Typography>
          <SearchProvider
            data={pods}
            urlKey="workload-pods-table"
            isLoading={isLoading}
          >
            <Search />
            <AppliedPodsTable isOptimizationDisabled={isOptimizationDisabled} />
          </SearchProvider>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
};

export const useAppliedPodsDrawer = () => {
  const { open } = useDrawer();
  return {
    openAppliedPodsDrawer: (props: BaseProps) => {
      open({
        component: AppliedPodsDrawer,
        props: { ...props },
      });
    },
  };
};
